import React from "react";

class FeraTbodyTd extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      editing: false,
      draft:
        this.props.header.onFormat
          ? this.props.header.onFormat(
              this.props.item[this.props.header.source],
              this.props.item
            )
          : this.props.item[this.props.header.source],
      value:
        this.props.item[this.props.header.source],
      saving: false,
      error: null,
    };
    this.inputRef = React.createRef();
  }

  get cell() {
    // Raw (unformatted) value for saving
    return this.props.item[this.props.header.source];
  }

  get formatted() {
    // For non-edit display
    return this.props.header.onFormat
      ? this.props.header.onFormat(this.cell, this.props.item)
      : this.cell;
  }

  get rendered() {
    // Custom render wins over default HTML
    return this.props.header.onRender
      ? this.props.header.onRender(this.cell, this.props.item)
      : null;
  }

  componentDidUpdate(prevProps, prevState) {
    // If row data changed externally, refresh value/draft
    if (prevProps.item !== this.props.item) {
      const next = this.props.item[this.props.header.source];
      this.setState({ value: next, draft: next });
    }
    // Focus when we enter edit mode
    if (!prevState.editing && this.state.editing && this.inputRef.current) {
      this.inputRef.current.focus();
      // Place caret at end
      const el = this.inputRef.current;
      if (el.setSelectionRange && typeof this.state.draft === "string") {
        const len = this.state.draft.length;
        el.setSelectionRange(len, len);
      }
    }
  }

  startEdit = () => {
    if (!this.props.header?.editable || this.state.saving) return;
    this.setState({ editing: true, draft: this.state.value, error: null });
  };

  cancelEdit = () => {
    this.setState({ editing: false, draft: this.state.value, error: null });
  };

  commitEdit = async () => {
    const newValue = this.state.draft;
    const { header, item } = this.props;
    if (!header?.editable) return;

    // If nothing changed, just exit
    if (newValue === this.state.value) {
      this.setState({ editing: false, error: null });
      return;
    }

    try {
      this.setState({ saving: true, error: null });
      if (typeof header.onEditAsync === "function") {
        // Awaitable path
        const saved = await header.onEditAsync(item, newValue);
        // Allow the async handler to return the finalized value (e.g., server normalization)
        const finalVal = saved !== undefined ? saved : newValue;
        this.setState({ value: finalVal, editing: false, saving: false });
      } else if (typeof header.onEdit === "function") {
        header.onEdit(item, newValue);
        this.setState({ value: newValue, editing: false, saving: false });
      } else {
        // No handler; just update local cell view
        this.setState({ value: newValue, editing: false, saving: false });
      }
    } catch (err) {
      this.setState({
        saving: false,
        error: err?.message || "Save failed",
      });
    }
  };

  onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      this.commitEdit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      this.cancelEdit();
    }
  };

  renderReadOnlyCell() {
    const className = `${this.props.header.dataClassName || "list-item-heading body"} ${this.props.header?.editable ? "cursor-text" : ""}`;
    if (this.rendered) {
      // Custom rendered content (non-HTML-string)
      return (
        <td
          colSpan={this.props.header.colspan || 1}
          role="cell"
          className={className}
          onDoubleClick={this.startEdit}
          onClick={this.props.header?.editOnClick ? this.startEdit : undefined}
          title={this.props.header?.editable ? "Double-click to edit" : undefined}
        >
          {this.rendered}
        </td>
      );
    }
    // Default path: formatted string, possibly HTML; keep your existing HTML rendering
    return (
      <td
        colSpan={this.props.header.colspan || 1}
        role="cell"
        className={className}
        onDoubleClick={this.startEdit}
        onClick={this.props.header?.editOnClick ? this.startEdit : undefined}
        title={this.props.header?.editable ? "Double-click to edit" : undefined}
        dangerouslySetInnerHTML={{ __html: this.formatted }}
      />
    );
  }

  renderEditCell() {
    const className = `${this.props.header.dataClassName || "list-item-heading body"}`;
    // Basic text input; swap for a textarea or specialized editor if needed
    return (
      <td colSpan={this.props.header.colspan || 1} role="cell" className={className}>
        <input
          ref={this.inputRef}
          className="form-control form-control-sm"
          value={this.state.draft ?? ""}
          onChange={(e) => this.setState({ draft: e.target.value })}
          onKeyDown={this.onKeyDown}
          onBlur={this.commitEdit}
          disabled={this.state.saving}
        />
        {this.state.error ? (
          <div className="text-danger small mt-1">{this.state.error}</div>
        ) : null}
      </td>
    );
  }

  render() {
    if (this.props.header?.editable && this.state.editing) {
      return this.renderEditCell();
    }
    return this.renderReadOnlyCell();
  }
}

export default FeraTbodyTd;